import type * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from "@mui/material";
import { withStyles } from "@mui/styles";
import AddIcon from "@mui/icons-material/Add";
import ProfileFormAccuracyAttestationBlock from "components/v3/forms/formComponents/ProfileFormAccuracyAttestationBlock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import GLSelect from "components/v3/forms/formComponents/GLSelect";
import GLTextField from "components/v3/forms/formComponents/GLTextField";
import {
  FormFieldContents,
  pickSubsetOfAttributes,
  useFormFieldState
} from "components/v3/forms/formComponents/FormUtils";
import GLEmailField from "components/v3/forms/formComponents/GLEmailField";
import GLPhoneField from "components/v3/forms/formComponents/GLPhoneField";
import { legalNameTooltip } from "components/v3/forms/formComponents/userInfoTooltips";
import GLAgePicker, {
  validateMemberDobMinAge
} from "components/v3/forms/formComponents/GLAgePicker";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import {
  selectKitsSelectedToBeOrdered,
  selectTestKitsGLIsCurrentlyOffering
} from "store/reducers/order/OrderInformationSlice";
import {
  selectUserPersonalInformation,
} from "store/reducers/user/UserAccountSlice";
import {
  type MemberApiPATCHPRofilePayload,
} from "apis/v3/memberApi";
import {
  GENDERS,
  PREFERRED_CONTACT_METHODS,
  PREFERRED_CONTACT_TIMES,
  PRONOUNS,
  SEXES
} from "constants/v3/generalConstants";
import GLStateSelect from "components/v3/forms/formComponents/GLStateSelect";
import GLZipField from "components/v3/forms/formComponents/GLZipField";
import { testNameImageMap } from "components/v3/sharedComponents/SuggestedScreeningCard";
const Accordion = withStyles({
  root: {
    background: "none",
    boxShadow: "none",
    margin: 0,
    "&.Mui-expanded": {
      "&::before": {
        opacity: 1
      }
    },
    "&::before": {
      backgroundColor: "transparent"
    },
    "& .MuiAccordionSummary-content": {
      margin: 0
    }
  }
})(MuiAccordion);
const AccordionExpandIcon = () => {
  return (
    <Box
      sx={{
        ".Mui-expanded & > .collapsIconWrapper": {
          display: "none"
        },
        ".expandIconWrapper": {
          display: "none"
        },
        ".Mui-expanded & > .expandIconWrapper": {
          display: "block"
        }
      }}
    >
      <div className="expandIconWrapper">
        <RemoveIcon sx={{ height: "24px", width: "24px" }} />
      </div>
      <div className="collapsIconWrapper">
        <AddIcon sx={{ height: "24px", width: "24px" }} />
      </div>
    </Box>
  );
};

export interface PersonalInfoFields {
  [key: string]: FormFieldContents;
  dob?: string;
  preferredName?: string;
  sexAssignedAtBirth?: string;
  gender?: string;
  pronouns?: string;
  email?: string;
  phone?: string;
  preferredContactMechanism?: "phone" | "email" | "either";
  preferredContactTime?: "morning" | "afternoon" | "either";
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

interface PersonalInfoProps {
  onFormFieldsUpdated: (formFields: PersonalInfoFields) => void;
  onSubmitted: (formFields: MemberApiPATCHPRofilePayload) => void;
}

type Kit = {
  lab: string;
  test_name: string;
  test_uuid: string;
};

const PersonalInfoForm: React.FC<PersonalInfoProps> = ({
  onFormFieldsUpdated,
  onSubmitted
}) => {
  const memberInformation = useSelector(selectUserPersonalInformation);
  const kitsGLCurrentOffers = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const selectedKitsUuids = useSelector(selectKitsSelectedToBeOrdered);
  const selectedKits = useMemo(() => {
    return kitsGLCurrentOffers.filter((offer: Kit) =>
      selectedKitsUuids?.includes(offer?.test_uuid)
    );
  }, [kitsGLCurrentOffers, selectedKitsUuids]);
  const hasSelectedKits = selectedKits?.length > 0;
  const memberHasExistingDob = Boolean(memberInformation.dob?.trim());
  const dataToPrePopulateForm: PersonalInfoFields = pickSubsetOfAttributes(
    memberInformation,
    [
      "dob",
      "preferredName",
      "sexAssignedAtBirth",
      "gender",
      "pronouns",
      "email",
      "phone",
      "preferredContactMechanism",
      "preferredContactTime",
      "addressLine1",
      "addressLine2",
      "city",
      "state",
      "postalCode"
    ]
  );

  const { formFields, formStatus, handleInputChange } = useFormFieldState<
    PersonalInfoFields
  >(dataToPrePopulateForm, onFormFieldsUpdated);

  const [submitting, setSubmitting] = useState(false);
  const [
    accuracyAttestationIsChecked,
    setAccuracyAttestationIsChecked
  ] = useState(false);

  useEffect(() => {
    if (!memberHasExistingDob) {
      const dobValue = String(formFields.dob ?? "");
      const empty = !dobValue.trim();
      const ageInvalid = Boolean(
        dobValue.trim() && validateMemberDobMinAge(dobValue)
      );
      handleInputChange({
        e: {
          target: { name: "dob", value: dobValue }
        } as React.ChangeEvent<HTMLInputElement>,
        isInvalid: empty || ageInvalid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formIsErrorFree =
    formStatus.invalidFields.filter(
      (field) => !(memberHasExistingDob && field === "dob")
    ).length === 0;
  const formCanBeSubmitted = formIsErrorFree && accuracyAttestationIsChecked;

  const handleSubmit = (): void => {
    setSubmitting(true);
    setTimeout(() => {
      onSubmitted(formFields);
    }, 0);
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        backgroundColor: COLORS.WHITE,
        borderRadius: 8,
        marginTop: 0,
        padding: {
          xs: "32px 16px",
          md: 4
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "5px"
          }}
        >
          <Typography
            sx={{ typography: { xs: "body_strong_lg", md: "body_strong_xl" } }}
          >
            Member Details
          </Typography>
          <Typography variant="body_sm_dark_gray">
            All fields required unless noted as optional.
          </Typography>
        </Box>
        <Box
          sx={{ background: COLORS.ACCENT3, height: "1px", width: "100%" }}
        ></Box>
        {hasSelectedKits && (
          <>
            <Accordion disableGutters={true}>
              <AccordionSummary
                expandIcon={<AccordionExpandIcon />}
                sx={{ margin: 0, padding: 0 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    margin: 0
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px"
                    }}
                  >
                    <Typography variant="body_strong_lg_primary">
                      Request Details
                    </Typography>
                    <Typography variant="body_sm_dark_gray">
                      This information will go to trusted partners who may
                      contact you to support your request for DNA testing.
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  paddingBottom: 1,
                  paddingTop: 3,
                  paddingX: 0
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: "15px"
                  }}
                >
                  {selectedKits.map(
                    ({
                      lab: currentKitLab,
                      test_name: currentKitName
                    }: Kit) => {
                      const currentKitImage = testNameImageMap[currentKitName];

                      return (
                        <Box
                          sx={{
                            alignItems: "center",
                            background: COLORS.ACCENT1_LIGHTEST,
                            borderRadius: "10px",
                            display: "flex",
                            gap: "10px",
                            padding: "30px 30px 30px 20px"
                          }}
                        >
                          <Box
                            sx={{
                              alignItems: "flex-start",
                              display: "flex",
                              gap: "10px"
                            }}
                          >
                            <Box
                              component="img"
                              alt={currentKitName}
                              src={currentKitImage}
                              sx={{
                                height: "40px",
                                width: "40px"
                              }}
                            />
                            <Box
                              sx={{
                                alignItems: "flex-start",
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px"
                              }}
                            >
                              <Typography variant="body_strong">
                                {currentKitName}
                              </Typography>
                              <Typography variant="body_xs_dark_gray">
                                <Box sx={{ display: "inline" }}>
                                  Our laboratory partner
                                </Box>{" "}
                                <Typography
                                  sx={{ display: "inline" }}
                                  variant="body_strong_xs_dark_gray"
                                >
                                  {currentKitLab},
                                </Typography>{" "}
                                <Box sx={{ display: "inline" }}>
                                  will perform your test
                                </Box>
                              </Typography>
                              <Box sx={{ alignItems: "center", display: "flex", gap: "4px" }}>
                                <CheckCircleOutlineIcon sx={{color: COLORS.ACCENT3, height: "24px", width: "24px" }} />
                                <Typography variant="body_xs_accent3">
                                  Consent signed
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Box
              sx={{ background: COLORS.GRAY, height: "1px", width: "100%" }}
            ></Box>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                md: "row"
              },
              gap: "16px"
            }}
          >
            <GLTextField
              disabled
              required
              id="userName"
              label="Your Name"
              value={`${memberInformation.firstName} ${memberInformation.lastName}`}
              tooltip={legalNameTooltip}
            />
            <GLAgePicker
              disabled={memberHasExistingDob}
              value={
                memberHasExistingDob
                  ? String(memberInformation.dob ?? "")
                  : String(formFields.dob ?? "")
              }
              onChange={memberHasExistingDob ? undefined : handleInputChange}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "5px"
            }}
          >
            <GLTextField
              id="preferredName"
              onChange={handleInputChange}
              value={formFields.preferredName}
              label="Would you prefer to be called a different name?"
              maxLength={13}
            />
            <Typography variant="body_xs_dark_gray">
              This is the name we will reference you by throughout your journey
              with Genomic Life. You can change this at any time.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "20px",
            flexDirection: {
              xs: "column",
              md: "row"
            },
            "& > *": {
              flexGrow: 1,
              minWidth: 0
            }
          }}
        >
          <GLSelect
            required
            id="sexAssignedAtBirth"
            onChange={handleInputChange}
            label="Sex assigned at Birth"
            options={SEXES}
            value={formFields.sexAssignedAtBirth}
            tooltip="Some health conditions affect males and females differently. Providing this information allows us to better tailor your personalized health guidance and action plans."
          />
          <GLSelect
            id="gender"
            onChange={handleInputChange}
            label="Gender Identity"
            options={GENDERS}
            value={formFields.gender}
          />
          <GLSelect
            id="pronouns"
            onChange={handleInputChange}
            label="Pronouns"
            options={PRONOUNS}
            value={formFields.pronouns}
          />
        </Box>
        <Typography variant="body_xs_dark_gray" mt={-2}>
          So we can provide age and sex appropriate screening-related
          information throughout your experience with Genomic Life.
        </Typography>
        <Typography variant="body_strong_lg_primary" mt="8px">
          Contact Details
        </Typography>
        <Typography variant="body_sm_dark_gray" mt={-2}>
          This is where we will send updates and information. The email you use
          to access Genomic Life’s portal won’t be changed.
        </Typography>

        <GLEmailField
          required
          label="Contact Email Address"
          id="email"
          onChange={handleInputChange}
          value={formFields.email}
        />
        <Typography variant="body_xs_dark_gray" mt={-2}>
          This is where we will send updates and information. The email you use
          to access Genomic Life's portal won't be changed.
        </Typography>

        <GLPhoneField
          required
          label="Contact Phone Number"
          id="phone"
          onChange={handleInputChange}
          value={formFields.phone}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row"
            },
            gap: "20px",
            "& > *": {
              flexGrow: 1,
              minWidth: 0
            }
          }}
        >
          <GLSelect
            required
            id="preferredContactMechanism"
            onChange={handleInputChange}
            label="I prefer"
            options={PREFERRED_CONTACT_METHODS}
            value={formFields.preferredContactMechanism}
          />
          <GLSelect
            required
            id="preferredContactTime"
            onChange={handleInputChange}
            label="Best time to contact"
            options={PREFERRED_CONTACT_TIMES}
            value={formFields.preferredContactTime}
          />
        </Box>
        <Typography variant="body_xs_dark_gray" mt={-2}>
          Genomic Life may reach out to you by email, phone or text with
          information that is important to your health.
        </Typography>

        <Typography variant="h6_primary" fontWeight="bold" mt={2}>
          Shipping Address
        </Typography>
        <Typography variant="body_sm_dark_gray" mt={-2} mb={0}>
          Please provide your preferred mailing address for accurate and timely
          deliveries as well as the best customer service and support.{" "}
          <strong>We accept US addresses only, no P.O. boxes.</strong>
        </Typography>

        <GLTextField
          required
          label="Street Address"
          id="addressLine1"
          onChange={handleInputChange}
          value={formFields.addressLine1}
        />
        <GLTextField
          label="Address Line 2"
          id="addressLine2"
          onChange={handleInputChange}
          value={formFields.addressLine2}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row"
            },
            gap: "20px",
            "& > *": {
              flexGrow: 1,
              minWidth: 0
            }
          }}
        >
          <GLTextField
            required
            label="City"
            id="city"
            onChange={handleInputChange}
            value={formFields.city}
          />
          <GLStateSelect
            required
            label="State"
            id="state"
            onChange={handleInputChange}
            value={formFields.state}
          />
          <GLZipField
            required
            label="Zip Code"
            id="postalCode"
            onChange={handleInputChange}
            value={formFields.postalCode}
          />
        </Box>
        <ProfileFormAccuracyAttestationBlock
          accuracyAttestationIsChecked={accuracyAttestationIsChecked}
          showAttestationCheckbox={formIsErrorFree}
          onAccuracyAttestationChange={() =>
            setAccuracyAttestationIsChecked(!accuracyAttestationIsChecked)
          }
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            width: "100%"
          }}
        >
          <GLButton
            text="Save & Send Request"
            onClick={handleSubmit}
            buttonType="primary"
            loading={submitting}
            disabled={!formCanBeSubmitted}
            rightIcon={<ArrowForwardIcon />}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalInfoForm;
